import "./video-playback-manager.css";

const managedVideos = new Set();
let activeVideo = null;

function getStage(video) {
  return video?.closest(
    ".why-carthy-video-stage, .compliance-video-stage, .sectors-video-stage",
  );
}

function ensureLoader(video) {
  const stage = getStage(video);
  if (!stage || stage.querySelector(".carthy-video-loader")) return;

  const loader = document.createElement("div");
  loader.className = "carthy-video-loader";
  loader.setAttribute("aria-hidden", "true");
  loader.innerHTML = '<span class="carthy-video-loader-ring"></span>';
  stage.appendChild(loader);
}

function setVideoState(video, state) {
  if (!video) return;
  video.dataset.carthyPerformance = "managed-video";
  video.dataset.carthyPerformanceState = state;

  const stage = getStage(video);
  if (stage) {
    stage.dataset.carthyPerformance = "video-stage";
    stage.dataset.carthyPerformanceState = state;
  }
}

function broadcastState() {
  const playing = Boolean(activeVideo && !activeVideo.paused);
  document.documentElement.dataset.carthyVideoPerformance = playing ? "playing" : "idle";
  window.dispatchEvent(
    new CustomEvent("carthy-video-performance", {
      detail: { playing },
    }),
  );
}

function pauseVideo(video) {
  if (!video) return;
  if (!video.paused) video.pause();
  setVideoState(video, "paused");
  if (activeVideo === video) activeVideo = null;
  broadcastState();
}

function playVideo(video) {
  if (!video || document.hidden) return;

  managedVideos.forEach((otherVideo) => {
    if (otherVideo !== video) pauseVideo(otherVideo);
  });

  activeVideo = video;
  video.preload = "auto";
  setVideoState(video, video.readyState >= 3 ? "starting" : "loading");
  broadcastState();

  const playPromise = video.play();
  if (!playPromise?.then) return;

  playPromise
    .then(() => {
      if (activeVideo === video && !video.paused) {
        setVideoState(video, "active");
        broadcastState();
      }
    })
    .catch(() => {
      if (activeVideo === video) activeVideo = null;
      setVideoState(video, video.error ? "error" : "paused");
      broadcastState();
    });
}

export function manageLoopingVideo(video, visibilityTarget = video) {
  if (!video || !visibilityTarget) return () => {};

  video.removeAttribute("autoplay");
  video.autoplay = false;
  video.muted = true;
  video.defaultMuted = true;
  video.playsInline = true;
  video.disablePictureInPicture = true;
  video.preload = "metadata";

  managedVideos.add(video);
  ensureLoader(video);
  setVideoState(video, "paused");
  broadcastState();

  let shouldPlay = false;

  const onLoadStart = () => {
    if (shouldPlay) setVideoState(video, "loading");
  };

  const onWaiting = () => {
    if (shouldPlay) setVideoState(video, "buffering");
  };

  const onCanPlay = () => {
    if (shouldPlay && video.paused) playVideo(video);
  };

  const onPlaying = () => {
    if (!shouldPlay) {
      pauseVideo(video);
      return;
    }
    activeVideo = video;
    setVideoState(video, "active");
    broadcastState();
  };

  const onError = () => {
    if (activeVideo === video) activeVideo = null;
    setVideoState(video, "error");
    broadcastState();
  };

  video.addEventListener("loadstart", onLoadStart);
  video.addEventListener("waiting", onWaiting);
  video.addEventListener("stalled", onWaiting);
  video.addEventListener("loadeddata", onCanPlay);
  video.addEventListener("canplay", onCanPlay);
  video.addEventListener("playing", onPlaying);
  video.addEventListener("error", onError);

  const observer = new IntersectionObserver(
    (entries) => {
      const entry = entries[0];
      shouldPlay = Boolean(entry?.isIntersecting && entry.intersectionRatio >= 0.12);

      if (shouldPlay) {
        playVideo(video);
      } else {
        pauseVideo(video);
      }
    },
    {
      threshold: [0, 0.12, 0.25, 0.5, 0.75],
      rootMargin: "100px 0px 100px 0px",
    },
  );

  observer.observe(visibilityTarget);

  const onVisibilityChange = () => {
    if (document.hidden) {
      pauseVideo(video);
    } else if (shouldPlay) {
      playVideo(video);
    }
  };

  document.addEventListener("visibilitychange", onVisibilityChange);

  return () => {
    observer.disconnect();
    document.removeEventListener("visibilitychange", onVisibilityChange);
    video.removeEventListener("loadstart", onLoadStart);
    video.removeEventListener("waiting", onWaiting);
    video.removeEventListener("stalled", onWaiting);
    video.removeEventListener("loadeddata", onCanPlay);
    video.removeEventListener("canplay", onCanPlay);
    video.removeEventListener("playing", onPlaying);
    video.removeEventListener("error", onError);
    pauseVideo(video);
    managedVideos.delete(video);
    broadcastState();
  };
}
